import { attr, html, MapSignal, on, prop } from '@tempots/dom'
import { RESUME_UPLOAD_ACCEPT } from '@rh/shared/constants/resume-content-types'
import { getWizardPdf } from '../pdf-extract/get-pdf'
import type { WizardState } from '../wizard-state'

export function UploadStep(state: WizardState) {
  const busy = prop(false)
  const error = prop<string | null>(null)
  const dragover = prop(false)

  const handleFile = async (file: File) => {
    if (busy.value) return
    busy.set(true)
    error.set(null)
    try {
      const pdf = await getWizardPdf(file)
      state.pdfData.set(pdf.data)
      state.pdfSourceName.set(file.name)
      state.extraction.set(null)
      state.step.set('bands')
    } catch (e) {
      error.set(
        e instanceof Error
          ? e.message
          : 'Something went wrong reading that file.'
      )
    } finally {
      busy.set(false)
    }
  }

  const browse = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = `${RESUME_UPLOAD_ACCEPT},.pdf,.doc,.docx`
    input.onchange = () => {
      const file = input.files?.[0]
      if (file) void handleFile(file)
    }
    input.click()
  }

  return html.div(
    // The drop target must NOT re-render while a drag is in flight: class
    // changes go through a reactive attribute, never a MapSignal rebuild —
    // replacing the element mid-drag detaches the dragover/drop handlers
    // and the browser falls back to opening the file in a new tab.
    html.div(
      attr.class(
        dragover.map((o): string => (o ? 'wiz-drop dragover' : 'wiz-drop'))
      ),
      on.click(browse),
      // stopPropagation: a drop that reaches document would be claimed by
      // any page-level drop listener (e.g. beatui's PageDropZone) — the
      // wizard owns drops on its own target.
      on.dragover((e: DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        dragover.set(true)
      }),
      on.dragleave(() => dragover.set(false)),
      on.drop((e: DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        dragover.set(false)
        const file = e.dataTransfer?.files?.[0]
        if (file) void handleFile(file)
      }),
      MapSignal(busy, isBusy =>
        isBusy
          ? html.div(
              attr.style('pointer-events:none;'),
              html.div(
                attr.style('font-size:15px; font-weight:600;'),
                'Reading your document…'
              ),
              html.div(
                attr.class('wiz-hint'),
                attr.style('margin-top:6px;'),
                'Word documents are converted to PDF first — this can take a few seconds.'
              )
            )
          : html.div(
              attr.style('pointer-events:none;'),
              html.div(attr.style('font-size:34px; line-height:1;'), '📤'),
              html.div(
                attr.style('font-size:15px; font-weight:600; margin-top:10px;'),
                'Drop a branded resume here, or click to browse'
              ),
              html.div(
                attr.class('wiz-hint'),
                attr.style('margin-top:6px;'),
                'PDF or Word. PDFs are read right here in your browser and never uploaded; Word documents are uploaded briefly so we can convert them.'
              )
            )
      )
    ),
    MapSignal(error, err =>
      err ? html.div(attr.class('wiz-error'), err) : html.span()
    ),
    html.p(
      attr.class('wiz-hint'),
      attr.style('margin-top:14px;'),
      'Tip: any document with your firm’s header on page 1 works — a resume you’ve already sent out, your letterhead, or a template. ',
      html.b('Graphics-heavy Word headers?'),
      ' Export the document as PDF and upload that instead — Word conversion can drop layered artwork, and a PDF preserves it exactly.'
    )
  )
}
